/**
 * Analytics Page
 *
 * Displays survey response analytics with charts and data tables.
 */

import React, { useState, useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import * as responsesApi from './api/responses'
import { exportAnalytics } from './utils/exportAnalytics'
import ResponseMap from './components/ResponseMap'
import { destroyCharts, renderCharts } from './utils/analyticsCharts'
import './App.css'
import './Analytics.css'

const PAGE_SIZE = 50

function Analytics() {
  const { id } = useParams()
  const { user, userRole, currentOrg, loading: authLoading } = useAuth()
  const [surveys, setSurveys] = useState([])
  const [activeId, setActiveId] = useState(id || '')
  const [survey, setSurvey] = useState(null)
  const [responses, setResponses] = useState([])
  const [responsesTotal, setResponsesTotal] = useState(0)
  const [responsesPage, setResponsesPage] = useState(0)
  const [responsesLoading, setResponsesLoading] = useState(false)
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('charts')
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState('')
  const [chartsLoading, setChartsLoading] = useState(false)
  const [mapResponses, setMapResponses] = useState([])

  const chartsRef = useRef(null)
  const chartInstancesRef = useRef([])
  const exportDropdownRef = useRef(null)
  const fullResponsesRef = useRef(null)

  // Check if user can export (owner or administrator only)
  const canExport = userRole === 'owner' || userRole === 'administrator'

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const chartInstances = chartInstancesRef.current
    return () => destroyCharts(chartInstances)
  }, [])

  // Handle export
  const handleExport = async (format) => {
    setExportDropdownOpen(false)
    setExporting(true)
    setExportProgress('')

    try {
      let exportResponses = responses

      // For Excel, fetch ALL responses (not just current page)
      if (format === 'excel' && responsesTotal > responses.length) {
        setExportProgress('Fetching all responses...')
        exportResponses = await responsesApi.fetchAllResponses(activeId, {
          completeOnly: true,
          onProgress: ({ fetched, total }) => {
            setExportProgress(`Fetching responses... ${fetched}/${total}`)
          },
        })
      }

      await exportAnalytics(format, {
        survey,
        analytics,
        responses: exportResponses,
        chartsContainer: chartsRef.current,
      })
    } catch (err) {
      console.error('Export failed:', err)
      alert('Export failed. Please try again.')
    } finally {
      setExporting(false)
      setExportProgress('')
    }
  }

  // Handle responses page change (lazy fetch)
  const handlePageChange = async (newPage) => {
    if (responsesLoading || !activeId) return
    setResponsesLoading(true)
    try {
      const result = await responsesApi.listResponses(activeId, {
        completeOnly: true,
        limit: PAGE_SIZE,
        offset: newPage * PAGE_SIZE,
      })
      setResponses(result.items || [])
      setResponsesTotal(result.total || 0)
      setResponsesPage(newPage)
    } catch (err) {
      console.error('Failed to fetch responses page:', err)
    } finally {
      setResponsesLoading(false)
    }
  }

  // Fetch survey list
  useEffect(() => {
    if (authLoading || !user) return

    let cancelled = false
    ;(async () => {
      try {
        const params = { limit: 50 }
        if (currentOrg?.id) params.organization_id = currentOrg.id
        const result = await surveysApi.listSurveys(params)
        if (cancelled) return
        const list = result.items || []
        setSurveys(list)
        if (list.length > 0) {
          setActiveId((currentId) => currentId || list[0].id)
        }
        if (list.length === 0) {
          setLoading(false)
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load surveys:', e)
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user, authLoading, currentOrg])

  // Fetch analytics data
  useEffect(() => {
    if (!activeId) return

    let cancelled = false
    setLoading(true)
    setError('')
    setMapResponses([])
    setChartsLoading(false)
    fullResponsesRef.current = null
    ;(async () => {
      try {
        const [surveyData, analyticsData, responsesResult] = await Promise.all([
          surveysApi.getSurvey(activeId),
          responsesApi.getAnalytics(activeId).catch(() => null),
          responsesApi
            .listResponses(activeId, { completeOnly: true, limit: PAGE_SIZE, offset: 0 })
            .catch(() => ({ items: [], total: 0 })),
        ])

        if (cancelled) return

        setSurvey(surveyData)
        setAnalytics(analyticsData)
        setResponses(responsesResult.items || [])
        setResponsesTotal(responsesResult.total || (responsesResult.items || []).length)
        setResponsesPage(0)

        // Fetch the current survey's full data set; retain only this survey in memory.
        const pageOneData = responsesResult.items || []
        const total = responsesResult.total || pageOneData.length

        if (total > PAGE_SIZE) {
          setChartsLoading(true)
          responsesApi
            .fetchAllResponses(activeId, { completeOnly: true })
            .then((fullData) => {
              if (!cancelled) {
                fullResponsesRef.current = fullData
                setMapResponses(fullData)
              }
            })
            .catch((err) => {
              console.error('Failed to fetch full responses for charts:', err)
              if (!cancelled) {
                fullResponsesRef.current = null
                setMapResponses(pageOneData)
              }
            })
            .finally(() => {
              if (!cancelled) setChartsLoading(false)
            })
        } else {
          fullResponsesRef.current = pageOneData
          setMapResponses(pageOneData)
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load analytics')
          console.error('Failed to load analytics:', e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [activeId])

  useEffect(() => {
    if (activeTab !== 'charts' || !survey || loading || chartsLoading) return

    const chartData = fullResponsesRef.current || responses
    if (chartData.length === 0) {
      destroyCharts(chartInstancesRef.current)
      if (chartsRef.current) chartsRef.current.innerHTML = ''
      return
    }

    const timer = setTimeout(() => {
      renderCharts(chartsRef.current, chartInstancesRef.current, survey, chartData)
    }, 50)

    return () => clearTimeout(timer)
  }, [activeTab, chartsLoading, loading, responses, survey])

  // Get question label from definition
  const getQuestionLabel = (questionId) => {
    const questions = survey?.definition?.questions || []
    const question = questions.find((q) => q.id === questionId)
    return question?.label || questionId
  }

  // Get location from response - check multiple possible field locations
  const getLocation = (response) => {
    const geo =
      response.geolocation ||
      response.metadata?.geolocation ||
      response.client_info?.geolocation ||
      response.meta?.geolocation
    if (!geo) return '-'
    const parts = [geo.city, geo.region, geo.country || geo.country_name].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : '-'
  }

  // Render responses table with pagination
  const renderTable = () => {
    if (!responses || responses.length === 0) {
      return <div className="no-data">No responses yet.</div>
    }

    const questions = survey?.definition?.questions || []
    const questionIds = questions.map((q) => q.id)
    const allKeys = Array.from(new Set(responses.flatMap((r) => Object.keys(r.answers || {}))))
    const orderedKeys = [
      ...questionIds.filter((id) => allKeys.includes(id)),
      ...allKeys.filter((k) => !questionIds.includes(k)),
    ]

    // Check if any response has geolocation
    const hasGeo = responses.some(
      (r) =>
        r.geolocation ||
        r.metadata?.geolocation ||
        r.client_info?.geolocation ||
        r.meta?.geolocation
    )

    const startIdx = responsesPage * PAGE_SIZE + 1
    const endIdx = Math.min(startIdx + responses.length - 1, responsesTotal)
    const totalPages = Math.ceil(responsesTotal / PAGE_SIZE)

    return (
      <div className="table-wrapper">
        {/* Pagination header */}
        {responsesTotal > PAGE_SIZE && (
          <div className="responses-pagination-header">
            <span className="pagination-info">
              Showing {startIdx}–{endIdx} of {responsesTotal} responses
            </span>
            <div className="pagination-controls">
              <button
                className="btn btn-sm btn-outline"
                disabled={responsesPage === 0 || responsesLoading}
                onClick={() => handlePageChange(responsesPage - 1)}>
                ← Previous
              </button>
              <span className="pagination-page">
                Page {responsesPage + 1} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline"
                disabled={responsesPage >= totalPages - 1 || responsesLoading}
                onClick={() => handlePageChange(responsesPage + 1)}>
                Next →
              </button>
            </div>
          </div>
        )}

        {responsesLoading && <div className="responses-loading-overlay">Loading...</div>}

        <table className="responses-table">
          <thead>
            <tr>
              {hasGeo && <th>Location</th>}
              {orderedKeys.map((key) => (
                <th key={key}>{getQuestionLabel(key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {responses.map((response, idx) => (
              <tr key={response.id || idx}>
                {hasGeo && <td className="location-cell">{getLocation(response)}</td>}
                {orderedKeys.map((key) => {
                  const val = response.answers?.[key]
                  let display = ''
                  if (Array.isArray(val)) display = val.join(', ')
                  else if (typeof val === 'object') display = JSON.stringify(val)
                  else display = String(val ?? '')
                  return <td key={key}>{display}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination footer */}
        {responsesTotal > PAGE_SIZE && (
          <div className="responses-pagination-footer">
            <button
              className="btn btn-sm btn-outline"
              disabled={responsesPage === 0 || responsesLoading}
              onClick={() => handlePageChange(responsesPage - 1)}>
              ← Previous
            </button>
            <span className="pagination-page">
              Page {responsesPage + 1} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline"
              disabled={responsesPage >= totalPages - 1 || responsesLoading}
              onClick={() => handlePageChange(responsesPage + 1)}>
              Next →
            </button>
          </div>
        )}
      </div>
    )
  }

  const totalResponses = analytics?.total_responses || responses.length
  const completeResponses = analytics?.complete_responses || responses.length

  // Calculate unique locations
  const uniqueLocations = new Set()
  responses.forEach((r) => {
    const geo =
      r.geolocation || r.metadata?.geolocation || r.client_info?.geolocation || r.meta?.geolocation
    const country = geo?.country || geo?.country_name
    if (country) uniqueLocations.add(country)
  })
  const locationCount = uniqueLocations.size

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        {/* Header */}
        <div className="analytics-header">
          <div className="header-left">
            <Link to="/dashboard" className="back-link">
              ← Back to Dashboard
            </Link>
            <h1 className="analytics-page-title">Survey Analytics</h1>
          </div>
          <div className="header-right">
            <div className="survey-selector">
              <select value={activeId} onChange={(e) => setActiveId(e.target.value)}>
                {surveys.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title || 'Untitled'}
                  </option>
                ))}
              </select>
            </div>

            {/* Export Dropdown - Only for owner/administrator */}
            {canExport && (
              <div className="export-dropdown-container" ref={exportDropdownRef}>
                <button
                  className="btn btn-export"
                  onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                  disabled={exporting || loading || responses.length === 0}>
                  {exporting ? (
                    <>
                      <span className="export-spinner"></span>
                      {exportProgress || 'Exporting...'}
                    </>
                  ) : (
                    <>
                      📥 Export
                      <span className="dropdown-arrow">▼</span>
                    </>
                  )}
                </button>

                {exportDropdownOpen && (
                  <div className="export-dropdown-menu">
                    <button className="export-option" onClick={() => handleExport('pdf')}>
                      <span className="export-icon">📄</span>
                      <div className="export-option-text">
                        <span className="export-option-title">Export as PDF</span>
                        <span className="export-option-desc">Charts and summary report</span>
                      </div>
                    </button>
                    <button className="export-option" onClick={() => handleExport('ppt')}>
                      <span className="export-icon">📊</span>
                      <div className="export-option-text">
                        <span className="export-option-title">Export as PowerPoint</span>
                        <span className="export-option-desc">Presentation-ready slides</span>
                      </div>
                    </button>
                    <div className="export-divider"></div>
                    <button className="export-option" onClick={() => handleExport('excel')}>
                      <span className="export-icon">📋</span>
                      <div className="export-option-text">
                        <span className="export-option-title">Export Raw Data (Excel)</span>
                        <span className="export-option-desc">
                          All responses in spreadsheet format
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {authLoading || loading ? (
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p>Loading analytics...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="btn btn-primary">
              Retry
            </button>
          </div>
        ) : surveys.length === 0 ? (
          <div className="empty-state">
            <p>No surveys found. Create a survey first to see analytics.</p>
          </div>
        ) : !survey ? (
          <div className="empty-state">
            <p>Select a survey from the sidebar to view its analytics.</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="analytics-stats">
              <div className="analytics-stat">
                <span className="stat-value">{totalResponses}</span>
                <span className="stat-label">Total Responses</span>
              </div>
              <div className="analytics-stat">
                <span className="stat-value">{completeResponses}</span>
                <span className="stat-label">Complete</span>
              </div>
              <div className="analytics-stat">
                <span className="stat-value">
                  {totalResponses > 0 ? Math.round((completeResponses / totalResponses) * 100) : 0}%
                </span>
                <span className="stat-label">Completion Rate</span>
              </div>
              {locationCount > 0 && (
                <div className="analytics-stat">
                  <span className="stat-value">{locationCount}</span>
                  <span className="stat-label">Countries</span>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="analytics-tabs">
              <button
                className={`tab-btn ${activeTab === 'charts' ? 'active' : ''}`}
                onClick={() => setActiveTab('charts')}>
                📊 Charts
              </button>
              <button
                className={`tab-btn ${activeTab === 'responses' ? 'active' : ''}`}
                onClick={() => setActiveTab('responses')}>
                📋 Responses
              </button>
              {analytics?.field_agent_stats && analytics.field_agent_stats.length > 0 && (
                <button
                  className={`tab-btn ${activeTab === 'field_agents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('field_agents')}>
                  👥 Field Agents
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'charts' && (
                <>
                  {!chartsLoading && mapResponses.length > 0 && (
                    <ResponseMap responses={mapResponses} />
                  )}
                  {chartsLoading && (
                    <div className="charts-loading">
                      <div className="loading-spinner">
                        <div className="spinner-ring"></div>
                      </div>
                      <p>Loading full response data for charts...</p>
                    </div>
                  )}
                  <div className="charts-section" ref={chartsRef}>
                    {responses.length === 0 && !chartsLoading && (
                      <div className="no-data">
                        <p>No responses to visualize yet.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              {activeTab === 'responses' && (
                <div className="responses-section">{renderTable()}</div>
              )}
              {activeTab === 'field_agents' && analytics?.field_agent_stats && (
                <div className="field-agents-section">
                  <h3 style={{ marginBottom: '1rem' }}>🏆 Field Agent Leaderboard</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="responses-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Field Agent</th>
                          <th style={{ textAlign: 'center' }}>Responses</th>
                          <th style={{ textAlign: 'center' }}>Location Violations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.field_agent_stats.map((agent, idx) => (
                          <tr
                            key={agent.user_id}
                            style={{ background: idx === 0 ? '#fefce8' : undefined }}>
                            <td style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                              {idx === 0
                                ? '🥇'
                                : idx === 1
                                  ? '🥈'
                                  : idx === 2
                                    ? '🥉'
                                    : `#${idx + 1}`}
                            </td>
                            <td>
                              <div style={{ fontWeight: '600' }}>{agent.full_name || '—'}</div>
                              <div style={{ color: '#666', fontSize: '0.875rem' }}>
                                {agent.email}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '0.25rem 0.75rem',
                                  background: '#dcfce7',
                                  color: '#166534',
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                }}>
                                {agent.responses_count}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {agent.location_violations > 0 ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '0.25rem 0.75rem',
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                  }}>
                                  {agent.location_violations}
                                </span>
                              ) : (
                                <span style={{ color: '#666' }}>0</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Analytics
